import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import { Close, WarningAmber, Info, CheckCircle } from "@mui/icons-material";

/**
 * ConfirmationDialog - Custom confirmation dialog
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onConfirm - Callback when confirm is clicked
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message
 * @param {string} confirmText - Text for confirm button
 * @param {string} cancelText - Text for cancel button
 * @param {string} variant - Dialog variant: 'warning' | 'info' | 'success' | 'danger'
 */
export default function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "warning",
}) {
  const getVariantConfig = () => {
    switch (variant) {
      case "warning":
        return {
          icon: <WarningAmber sx={{ color: "warning.main", fontSize: 48 }} />,
          confirmColor: "warning.main",
          confirmHoverColor: "warning.dark",
        };
      case "danger":
        return {
          icon: <WarningAmber sx={{ color: "error.main", fontSize: 48 }} />,
          confirmColor: "error.main",
          confirmHoverColor: "error.dark",
        };
      case "success":
        return {
          icon: <CheckCircle sx={{ color: "success.main", fontSize: 48 }} />,
          confirmColor: "success.main",
          confirmHoverColor: "success.dark",
        };
      case "info":
      default:
        return {
          icon: <Info sx={{ color: "info.main", fontSize: 48 }} />,
          confirmColor: "info.main",
          confirmHoverColor: "info.dark",
        };
    }
  };

  const variantConfig = getVariantConfig();

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          color: "text.secondary",
        }}
      >
        <Close />
      </IconButton>

      <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center" }}>
        <Box sx={{ mb: 2 }}>{variantConfig.icon}</Box>
        <Typography variant="h5" fontWeight={600}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 3, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2, justifyContent: "center" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          fullWidth
          sx={{
            flex: 1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={variant === "danger" ? "error" : "primary"}
          size="large"
          fullWidth
          sx={{
            flex: 1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            bgcolor: variant === "warning" ? "warning.main" : undefined,
            "&:hover": {
              bgcolor: variant === "warning" ? "warning.dark" : undefined,
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
